<template>
  <FormCard
    data-testid="self-service-form-linkedin-group-linkedin-company-page"
    data-group-key="linkedin-company-page"
    :title="linkedInCompanyPageTitle"
    :description="selectedCompany ? selectedCompany.name : $t('campaigns.linkedin.linkedin_profile_description')"
    :completed="completedCards.has(`${linkedInCompanyPageTitle}`)"
    :class="{ 'selected-card': openCard === `${linkedInCompanyPageTitle}` }"
    :is-open="openCard === `${linkedInCompanyPageTitle}`"
    @toggle="emit('toggle', `${linkedInCompanyPageTitle}`)"
    @enter-pressed="handleSaveAndContinue">
    <template #actions>
      <button
        type="button"
        class="btn btn-icon"
        style="align-self: center"
        @click="openCompanyUrl"
        :disabled="!selectedCompanyUrl">
        <IconExternalLink v-tooltip="$t('campaigns.linkedin.open_in_linkedin')" size="20" stroke-width="1.5" />
      </button>
    </template>
    <template #form-content>
      <div class="form-section" data-testid="self-service-field-company_urn" data-field-key="company_urn">
        <div class="combobox-wrapper">
          <LeadCombobox
            v-model="selectedCompany"
            @search="onSearch"
            :getOptionValue="(option) => option.id"
            :getOptionLabel="(option) => option.name || ''"
            :items="companies"
            :loading="companiesLoading"
            :label="`${linkedInCompanyPageTitle}`"
            :disable-filtering="true">
            <template #hint>
              {{ $t('campaigns.linkedin.linkedin_profile_hint') }}
            </template>
          </LeadCombobox>
          <LeadButton variant="icon" class="external-link-btn" @click="openCompanyUrl" :disabled="!selectedCompanyUrl">
            <IconExternalLink v-tooltip="$t('campaigns.linkedin.open_in_linkedin')" size="20" stroke-width="1.5" />
          </LeadButton>
        </div>
      </div>
      <div
        class="float-right"
        data-testid="self-service-save-action-linkedin-company-page"
        data-i18n-key="btn.save_and_continue">
        <LeadButton :loading="loadingSave" variant="secondary" @click="handleSaveAndContinue">
          {{ $t('btn.save_and_continue') }}
          <template #icon>
            <IconArrowBigRightFilled size="16" stroke-width="1.5" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
</template>

<script setup lang="ts">
import FormCard from '@/components/orders/form_cards/FormCard.vue';
import LeadButton from '../generic/LeadButton.vue';
import { IconExternalLink, IconArrowBigRightFilled } from '@tabler/icons-vue';
import { ref, computed, watch, type Ref } from 'vue';
import { request } from '@/api/client';
import { debounce } from '@prospective/lithium';
import { updateCampaignObject } from '@/api/campaign';
import { useToast } from 'vue-toastification';
import translator from '@/locale/translator';
import { useProviderStore } from '@/stores/providers';
import LeadCombobox from './LeadCombobox.vue';
import { storeToRefs } from 'pinia';
import type { LinkedInForm } from '@/components/orders/details/provider_forms/LinkedInForm.vue';

const props = defineProps<{
  providerId: string | null | undefined;
  openCard: string | null;
  completedCards: Set<string>;
  object: any;
}>();

const emit = defineEmits<{
  (e: 'toggle', cardKey: string): void;
  (e: 'completed', currentCardKey: string, nextCardKey: string | null): void;
  (e: 'update:object', obj: any): void;
}>();

const companies = ref([]);
const companiesLoading = ref(false);
const selectedCompany = ref<{ id: string; name: string } | null>(null);
const toast = useToast();
const providerStore = useProviderStore();
const loadingSave = ref(false);
const { obj } = storeToRefs(providerStore) as unknown as { obj: Ref<LinkedInForm> };

const searchCompanies = async (search: string) => {
  if (!search) {
    companies.value = [];
    companiesLoading.value = false;
    return;
  }
  try {
    const res = await request('GET', `/v1/account/campaigns/${props.providerId}/options/linkedin_company_urns`, {
      query: search,
    });
    companies.value = res?.data || [];
  } finally {
    companiesLoading.value = false;
  }
};

const searchCompaniesDebounced = debounce(searchCompanies, 500);

function onSearch(search: string) {
  companiesLoading.value = true;
  searchCompaniesDebounced(search);
}

const selectedCompanyUrl = computed(() => {
  if (!selectedCompany.value) return null;
  // get just the id from the urn
  const companyId = selectedCompany.value.id.split(':').pop();
  return `https://www.linkedin.com/company/${companyId}`;
});

const linkedInCompanyPageTitle = computed(() => {
  if (!props.object?.groups) return 'LinkedIn Company Page';
  return props.object?.groups[0]?.name || 'LinkedIn Company Page';
});

const openCompanyUrl = () => {
  if (selectedCompanyUrl.value) {
    window.open(selectedCompanyUrl.value, '_blank');
  }
};

const handleSaveAndContinue = async () => {
  if (selectedCompany.value) {
    try {
      loadingSave.value = true;
      const res = await updateCampaignObject(props.providerId ?? '', {
        company_urn: selectedCompany.value.id,
        company_name: selectedCompany.value.name,
      });

      emit('update:object', res.data);
      setTimeout(() => {
        loadingSave.value = false;
        providerStore.addCompletedCard(`${linkedInCompanyPageTitle.value}`, props.providerId ?? '');
        emit('completed', `${linkedInCompanyPageTitle.value}`, `${props.object?.groups?.[1]?.name || null}`);
      }, 500);
    } catch (error) {
      console.error('Error updating campaign object:', error);
      toast.error(translator.get('campaigns.linkedin.error_saving_page'));
      loadingSave.value = false;
    }
  }
};

watch(
  () => [props.object?.groups?.[0]?.properties?.[0]?.value, props.object?.groups?.[0]?.properties?.[1]?.value],
  ([companyUrn, companyName]) => {
    if (companyUrn && companyName && (!selectedCompany.value || selectedCompany.value.id !== companyUrn)) {
      selectedCompany.value = { id: companyUrn, name: companyName };
    }
  },
  { immediate: true }
);

watch(selectedCompany, (newValue) => {
  if (newValue) {
    obj.value.company_urn = newValue.id;
    obj.value.company_name = newValue.name;
  } else {
    obj.value.company_urn = null;
    obj.value.company_name = null;
  }
});
</script>

<style scoped lang="scss">
.float-right {
  float: right;
}

.form-section {
  margin-bottom: 30px;
}
.combobox-wrapper {
  position: relative;
  padding-right: 56px;
}

.external-link-btn {
  position: absolute;
  top: 26px;
  right: 0;
}
</style>
